/**
 * NoticeListContainer
 * --
 */
import React, { useEffect, useReducer, useState } from 'react';
import NoticeListPresenter from './NoticeListPresenter';
import {
  useGetNotices,
  useCreateNotice,
  useUpdateNotice,
  useDeleteNotice,
} from 'hooks/NoticeHooks';
import { useUserStack } from 'store';
import MessageAlert from 'utils';

const NoticeListContainer = () => {
  /* ===== ROUTE ===== */

  /* ===== MUTATE ===== */
  const userInfo = useUserStack();
  const { createNotice } = useCreateNotice();
  const { updateNotice } = useUpdateNotice();
  const { deleteNotice } = useDeleteNotice();
  /* ===== DATA ===== */
  const { data: Notices, refetch } = useGetNotices();

  /* ===== PROPS ===== */
  /* ===== STATE ===== */
  const [isLoading, setIsLoading] = useState(false);
  const [data, setData] = useState([]);
  // 모달
  const [isOpened, setIsOpened] = useState(false);
  // 모달 데이터
  const [modalData, setModalData] = useState({
    notice: {},
  });
  // 모달 타입
  const [modalType, setModalType] = useState('read');

  // 전체 선택
  const [isCheckedAll, setIsCheckedAll] = useReducer((state, action) => {
    switch (action.type) {
      case 'TOGGLE':
        return !state;
      case 'RESET':
        return false;
      default:
        return state;
    }
  }, false);
  // 선택 목록
  const [checkedList, setCheckedList] = useState([]);

  // 리패칭
  const [isRefetch, setIsRefetch] = useState(false);

  /* ===== FUNCTION ===== */
  /**
   * 모달창 핸들러
   * --
   */
  const handleModalOpen = (data = {}) => {
    setModalData(data);
    setIsOpened(!isOpened);
  };

  /**
   * 모달 핸들러
   * --
   */
  const handleModal = (open, type = '', data = {}) => {
    setModalData((prev) => ({
      ...prev,
      [type]: data,
    }));
    setIsOpened(!open);
    setModalType(type);
  };
  /**
   * 체크박스 핸들러
   * --
   * @param { boolean } c
   * @param { object | array } d
   * @param { string } t
   */
  const handleChecked = (c, d, t = null) => {
    if (t) {
      // 전체 선택인 경우
      setCheckedList(d);
    } else {
      // 단일 선택인 경우
      if (c) {
        setCheckedList((prev) => [...prev, d]);
      } else {
        setCheckedList((prev) => prev.filter((item) => item.key !== d.key));
      }
    }
  };

  /**
   * 데이터 바인딩 함수
   * --
   * @param {string} target
   * @param {*} value
   * @param {object} object
   * @param {*} callback
   */
  const handleChange = (t, v, o, callback = null) => {
    let newData = modalData[o];

    newData[t] = v;
    const d = {
      ...modalData,
    };

    setModalData(d);
  };

  /**
   * 선택된 공지사항 삭제
   * --
   */
  const handleDeleteSelected = async () => {
    const noticeIds = checkedList.map((item) => item.notice_id);

    if (checkedList.length === 0) {
      MessageAlert.warning('삭제할 항목을 선택해주세요.');
      return;
    }

    try {
      setIsLoading(true);

      // 선택된 notice_id 리스트로 삭제 요청
      const deletePromises = checkedList.map((item) =>
        deleteNotice(item.notice_id),
      );

      await Promise.all(deletePromises);

      MessageAlert.success('선택된 공지사항이 삭제되었습니다.');

      // 선택 항목 초기화
      setCheckedList([]);
      // 리패치
      await refetch();
    } catch (err) {
      MessageAlert.error('공지사항 삭제 중 오류가 발생했습니다.');
      console.error('[DELETE NOTICE ERROR]:', err);
    } finally {
      setIsLoading(false);
    }
  };

  /**
   * 데이터 생성/수정
   * --
   * @param {string} t
   * @param {string} s
   * @returns
   */
  const handleSubmit = async (t, s = 'CREATE') => {
    // 생성 타입 대문자 변환
    const type = t.toLocaleUpperCase();
    // 함수 타입 대문자 변환
    const submit = s.toLocaleUpperCase();

    // 생성 타입 정의
    const submitType = {
      NOTICE: '공지사항',
      CREATE: '생성',
      UPDATE: '수정',
    };

    // NOTE: 데이터 유효성 검사 및 데이터 리턴
    const data = handleValidateCheck(type);
    if (!data) return;

    // NOTE: 생성/수정 함수 정의
    const submitFunc = {
      NOTICE: {
        CREATE: createNotice,
        UPDATE: updateNotice,
      },
    };

    try {
      // 로딩 처리
      setIsLoading(true);

      // 데이터 생성/수정
      const { status } = await submitFunc[type][submit]({
        notice_id: modalData?.notice?.notice_id,
        ...data,
      });

      // 데이터 업데이트 성공 시
      if (status === 200) {
        MessageAlert.success(
          `성공적으로 ${submitType[type]}이/가 ${submitType[submit]}되었습니다.`,
        );
        // 값 초기화
        setModalData((prev) => ({ ...prev, t: {} }));
        // 모달 닫기
        setIsOpened(!isOpened);
        // 리패치
        setIsRefetch(true);

        await refetch();
        return;
      }

      MessageAlert.warning(
        `${submitType[type]}을/를 ${submitType[submit]}하는데 실패하였습니다.`,
      );
    } catch (err) {
      MessageAlert.error(
        `${submitType[type]}을/를 ${submitType[submit]}하는데 오류가 발생하였습니다.`,
      );
      console.error(`[CREATE ${type}] ERROR : `, err);
      throw err;
    } finally {
      // 로딩 처리
      setTimeout(() => {
        setIsLoading(false);
      }, 500);
    }
  };

  /**
   * 유효성 검사
   * --
   * @param {string} t
   * @returns
   */
  const handleValidateCheck = (t) => {
    if (t === 'NOTICE') {
      // 유효성 검사
      const { notice_name, notice_content, is_show, show_target, is_fixed } =
        modalData?.notice || {};

      // 공지제목 입력값 체크
      if (!notice_name) {
        MessageAlert.warning('공지제목을 입력해주세요.');
        return false;
      }

      // 공지내용 입력값 체크
      if (!notice_content || notice_content === '<p><br></p>') {
        MessageAlert.warning('공지내용를 입력해주세요.');
        return false;
      }

      // 노출여부 체크
      if (!is_show) {
        MessageAlert.warning('노출여부를 선택해주세요.');
        return false;
      }

      // 타겟 체크
      if (!show_target) {
        MessageAlert.warning('타겟을 선택해주세요.');
        return false;
      }

      return {
        notice_name,
        notice_content,
        is_show,
        show_target,
        is_fixed,
      };
    }
  };

  /* ===== HOOKS ===== */
  /**
   * 모달 타입 초기화
   * --
   */
  useEffect(() => {
    if (!isOpened) setModalType('read');
  }, [isOpened]);

  /**
   * 데이터 초기화
   * --
   */
  useEffect(() => {
    const newData = Notices?.data
      ?.map((notice) => ({
        ...notice,
        // created_at: notice?.created_at,
        // updated_at: notice?.updated_at,
      }))
      .sort((a, b) => b.created_at - a.created_at);
    setData(newData);
    // eslint-disable-next-line
  }, [Notices]);
  /* ===== RENDER ===== */
  return (
    <>
      <NoticeListPresenter
        data={data}
        isCheckedAll={isCheckedAll}
        checkedList={checkedList}
        onChecked={handleChecked}
        onCheckedAll={setIsCheckedAll}
        isOpened={isOpened}
        modalData={modalData}
        modalType={modalType}
        onModalChange={setModalType}
        onClose={handleModalOpen}
        onOpen={handleModal}
        isRefetch={isRefetch}
        refetch={refetch}
        onChange={handleChange}
        onSubmit={handleSubmit}
        userInfo={userInfo}
        onDelete={handleDeleteSelected}
        setIsLoading={setIsLoading}
        isLoading={isLoading}
      />
    </>
  );
};

export default NoticeListContainer;
